import React, { useState } from "react";
import { toast } from "react-toastify";

import playerService from "../services/playerService";

const initialForm = {
  full_name: "",
  username: "",
  password: "",
  mobile: "",
};

const CreatePlayerModal = ({
  show,
  onClose,
  onPlayerCreated,
}) => {
  const [formData, setFormData] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  if (!show) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value } = event.target;

    if (name === "mobile") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setFormData((previous) => ({
        ...previous,
        mobile: digitsOnly,
      }));
    } else {
      setFormData((previous) => ({
        ...previous,
        [name]: value,
      }));
    }
  };

  const handleClose = () => {
    if (loading) {
      return;
    }

    setFormData(initialForm);
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = {
      full_name: formData.full_name.trim(),
      username: formData.username.trim(),
      password: formData.password,
      mobile: formData.mobile.trim(),
    };

    if (
      !payload.full_name ||
      !payload.username ||
      !payload.password
    ) {
      toast.error(
        "Full name, username and password are required."
      );
      return;
    }

    if (payload.username.length < 3) {
      toast.error(
        "Username must contain at least 3 characters."
      );
      return;
    }

    if (payload.password.length < 6) {
      toast.error(
        "Password must contain at least 6 characters."
      );
      return;
    }

    if (
      payload.mobile &&
      (payload.mobile.length !== 10 || !/^[6-9]\d{9}$/.test(payload.mobile))
    ) {
      toast.error(
        "Please enter a valid 10-digit mobile number (starting with 6, 7, 8, or 9)."
      );
      return;
    }

    try {
      setLoading(true);

      const response =
        await playerService.createPlayer(payload);

      if (!response?.success) {
        toast.error(
          response?.message ||
            "Unable to create Player."
        );
        return;
      }

      toast.success(
        response.message ||
          "Player created successfully."
      );

      setFormData(initialForm);

      await Promise.resolve(onPlayerCreated?.());

      onClose?.();
    } catch (error) {
      console.error("Create Player Error:", error);

      toast.error(
        error.response?.data?.message ||
          "Unable to create Player."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1050 }}
        onClick={handleClose}
      />

      <div
        className="modal fade show d-block"
        tabIndex="-1"
        role="dialog"
        aria-modal="true"
        style={{ zIndex: 1055, overflow: "hidden" }}
        onMouseDown={(event) => {
          if (event.target === event.currentTarget) {
            handleClose();
          }
        }}
      >
        <div
          className="modal-dialog modal-dialog-centered"
          style={{ maxWidth: "480px", margin: "10px auto" }}
        >
          <div className="modal-content border border-[rgba(148,163,184,0.2)] bg-[#121827] shadow-2xl rounded-2xl overflow-hidden text-[#F8FAFC]">
            {/* HEADER */}
            <div className="modal-header bg-[#0D111C] border-b border-[rgba(148,163,184,0.14)] py-3.5 px-4 d-flex align-items-center justify-content-between">
              <div className="d-flex align-items-center gap-2.5">
                <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30 text-lg font-bold">
                  <i className="bi bi-person-plus-fill" />
                </div>
                <div>
                  <h5 className="modal-title fw-bold text-[#F8FAFC] fs-6 mb-0">
                    Create New Player
                  </h5>
                  <div className="text-[10px] text-[#94A3B8]">
                    Player will be created under your Shop
                  </div>
                </div>
              </div>

              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={handleClose}
                disabled={loading}
                aria-label="Close"
              />
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body p-4 space-y-3.5">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Enter Player full name"
                    value={formData.full_name}
                    onChange={handleChange}
                    maxLength={100}
                    disabled={loading}
                    required
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
                    Username *
                  </label>
                  <input
                    type="text"
                    name="username"
                    className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Enter login username"
                    value={formData.username}
                    onChange={handleChange}
                    minLength={3}
                    maxLength={50}
                    autoComplete="off"
                    disabled={loading}
                    required
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
                    Password *
                  </label>
                  <input
                    type="password"
                    name="password"
                    className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Minimum 6 characters"
                    value={formData.password}
                    onChange={handleChange}
                    minLength={6}
                    autoComplete="new-password"
                    disabled={loading}
                    required
                  />
                </div>

                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#94A3B8] mb-1.5 block">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    name="mobile"
                    className="w-full rounded-xl border border-[rgba(148,163,184,0.16)] bg-[#0D111C] px-3.5 py-2.5 text-sm text-[#F8FAFC] placeholder:text-[#64748B] focus:border-[#D4AF37] focus:outline-none"
                    placeholder="Enter 10-digit mobile number"
                    value={formData.mobile}
                    onChange={handleChange}
                    maxLength={10}
                    inputMode="numeric"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* FOOTER */}
              <div className="modal-footer bg-[#0D111C] border-t border-[rgba(148,163,184,0.14)] py-3.5 px-4 d-flex justify-content-end gap-2.5">
                <button
                  type="button"
                  className="rounded-xl border border-[rgba(148,163,184,0.14)] bg-[#121827] px-4 py-2 text-xs font-semibold text-[#94A3B8] transition hover:bg-[#182033] hover:text-[#F8FAFC]"
                  onClick={handleClose}
                  disabled={loading}
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="rounded-xl bg-gradient-to-r from-[#D4AF37] to-[#AA7C11] px-5 py-2 text-xs font-extrabold text-[#070A12] shadow-md shadow-[#D4AF37]/10 transition hover:opacity-90 disabled:opacity-50 d-flex align-items-center gap-1.5"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-person-plus" />
                      Create Player
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default CreatePlayerModal;